import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Route, Redirect, Switch } from 'react-router-dom';
import styled from '@emotion/styled';
import { ToastContainer } from 'react-toastify';

import UnauthorizedRouter from './components/UnauthorizedRouter';
import PrivateRouter from './components/PrivateRouter';
import NotFoundPage from './components/NotFoundPage';
import ScrollToTop from './components/ScrollToTop';
import ThirdPartySignIn from './components/ThirdPartySignIn';
import LogoutPage from './components/LogoutPage';

import { setAuthToken, logout } from './actions/auth';

import { getRoutingByPrivileges, getBasicRedirectRoute } from './helpers/privileges';

import { UNAUTH_MAIN_PAGE, UNAUTH_PATHS } from './config';

const Wrapper = styled('div')`
  position: relative;
  width: 100%;
  height: auto;
  min-height: 100%;
  overflow-x: hidden;

  .logo {
    height: 100%;

    span.logo-lg {
      display: flex;
      align-items: center;
      padding-left: 20px;
      height: 100%;

      img {
        height: 40px;
      }
    }
  }

  .nav-link.without-border::after {
    content: none !important;
  }

  i.mdi-forum.reference-icon {
    position: relative;
    bottom: 1px;
    left: 1px;
  }

  @media (max-width: 768px) {
    .content-page {
      margin-top: 75px;
    }
  }
  @media (max-width: 550px) {
    .content-page {
      margin-top: 35px;
    }
  }
  @media (max-width: 1024px) {
    .content-page {
      margin-left: 0px;
    }
  }
`;

const UnauthRoute = ({ component: Component, isAuth, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      !isAuth ? (
        <>
          <ScrollToTop />
          <Component {...props} />
        </>
      ) : (
        <Redirect
          to={{
            pathname: '/start',
            state: { from: props.location },
          }}
        />
      )
    }
  />
);

const PrivateRoute = ({ component: Component, isAuth, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      isAuth ? (
        <>
          <ScrollToTop />
          <Component {...props} />
        </>
      ) : (
        <Redirect
          to={{
            pathname: UNAUTH_MAIN_PAGE,
            state: { from: props.location },
          }}
        />
      )
    }
  />
);

const Kak2cApp = (props) => {
  const { dispatch, state } = props;

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    (async function () {
      const localTokenData = localStorage.getItem('Kak2cTokenData');
      const localPrivilegesData = localStorage.getItem('Kak2cPrivileges')
        ? JSON.parse(localStorage.getItem('Kak2cPrivileges'))
        : null;
      if (!state.auth.tokenData && localTokenData) {
        if (!localPrivilegesData || !localPrivilegesData.length) {
          dispatch(logout());
        } else {
          const tokenData = JSON.parse(localTokenData);
          await dispatch(setAuthToken(tokenData));
        }
      }
      setIsReady(true);
    })();
  }, []);

  const isAuthenticated = state.auth.tokenData;
  const basicRedirectRoute = getBasicRedirectRoute(state.auth.privileges);

  return useMemo(
    () =>
      isReady ? (
        <BrowserRouter>
          <Wrapper>
            <Switch>
              <Route
                exact
                path="/"
                render={() =>
                  !isAuthenticated ? (
                    <Redirect to={UNAUTH_MAIN_PAGE} />
                  ) : basicRedirectRoute ? (
                    <Redirect to={basicRedirectRoute} />
                  ) : null
                }
              />
              {UNAUTH_PATHS.map((path) => (
                <UnauthRoute
                  key={path}
                  path={path}
                  component={UnauthorizedRouter}
                  isAuth={isAuthenticated}
                />
              ))}
              {state.auth.privileges
                ? getRoutingByPrivileges(state.auth.privileges).map((path) => (
                    <PrivateRoute
                      key={path}
                      path={`/${path}`}
                      component={PrivateRouter}
                      isAuth={isAuthenticated}
                    />
                  ))
                : null}

              {/* {PRIVATE_PATHS.map(path =>
                    <PrivateRoute key={path} path={path} component={PrivateRouter} isAuth={isAuthenticated} />
                  )} */}
              <Route exact path="/third_party_sign_in" component={ThirdPartySignIn} />
              <Route exact path="/logout" component={LogoutPage} />
              <Route>
                <ScrollToTop />
                <Redirect to={`/${basicRedirectRoute}`} />
                <NotFoundPage />
              </Route>
            </Switch>
            <ToastContainer autoClose={5000} draggable={false} />
          </Wrapper>
        </BrowserRouter>
      ) : null,
    [isReady, isAuthenticated, state.auth.privileges, basicRedirectRoute],
  );
};

export default Kak2cApp;
